import { useId } from "react";
import { useInvoicePdfTemplateSettings } from "@dolfin/ui";
import { toast } from "sonner";
import { invoicePdfTemplateValues, type components } from "@dolfin/api-admin";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Settings = components["schemas"]["OrganisationArSettingsResponse"];
type Template = components["schemas"]["InvoicePdfTemplate"];

type Props = {
  organisationId: string;
  settings: Settings | null;
  onSaved: (settings: Settings) => void;
  canEdit?: boolean;
};

export function InvoicePdfTemplateSettings({
  organisationId,
  settings,
  onSaved,
  canEdit = true,
}: Props) {
  const id = useId();
  const { value, setSelection, saving, error, submit } =
    useInvoicePdfTemplateSettings<Template, Settings>({
      savedValue: settings?.invoicePdfTemplate,
      canEdit,
      save: async (value) => {
        const result = await api.PATCH(
          "/admin/organisations/{id}/invoice-pdf-template",
          {
            params: { path: { id: organisationId } },
            body: { invoicePdfTemplate: value },
          },
        );
        if (result.error || !result.data) {
          const problem = result.error;
          const validation = problem && "errors" in problem && problem.errors;
          const details = validation
            ? Object.values(validation).flat().join(" ")
            : null;
          throw new Error(
            details ||
              problem?.detail ||
              problem?.title ||
              "Could not save the invoice PDF template.",
          );
        }
        return result.data;
      },
      onSaved: (result) => {
        onSaved(result);
        toast.success("Invoice PDF template saved");
      },
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice PDF template</CardTitle>
        <CardDescription>
          Choose the PDF layout for new invoices. Existing invoices keep their
          layout. Email templates are separate.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!settings ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load invoice settings. Reload the page to try again.
          </p>
        ) : (
          <>
            <Label htmlFor={id}>PDF layout</Label>
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={value}
                onValueChange={(next) => {
                  const template = invoicePdfTemplateValues.find(
                    (option) => option === next,
                  );
                  if (template) setSelection(template);
                }}
                disabled={!canEdit || saving}
              >
                <SelectTrigger
                  id={id}
                  className="w-56"
                  aria-describedby={error ? `${id}-error` : undefined}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {invoicePdfTemplateValues.map((template) => (
                    <SelectItem key={template} value={template}>
                      {template}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canEdit && (
                <Button
                  onClick={() => void submit()}
                  disabled={saving || value === settings.invoicePdfTemplate}
                >
                  {saving ? "Saving..." : "Save template"}
                </Button>
              )}
            </div>
            {!canEdit && (
              <p className="text-sm text-muted-foreground">
                An administrator can change this setting.
              </p>
            )}
          </>
        )}
        {error && (
          <p
            id={`${id}-error`}
            role="alert"
            className="text-sm text-destructive"
          >
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
